import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { useNavigate } from '#/hooks/use-navigate'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import { invitationAPI } from '#/api/invitation'
import { organizationAPI } from '#/api/organization'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { toast } from 'sonner'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#/components/ui/select'
import { Input } from '#/components/ui/input'


interface FormInputs {
  email: string
  level: 'admin' | 'member'
}

export default function NewInvitationModal() {
  const { t } = useTranslation([ 'team', 'general' ])
  const navigate = useNavigate()
  const params = useParams() as { organization: string }
  const queryClient = useQueryClient()
  const { data: grantableAccessLevels } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'grantable-access-levels' ],
    queryFn: () => organizationAPI.getGrantableAccessLevels(params.organization)
  })

  // Close the modal by navigating away
  const close = () => navigate('..')

  // Form state
  const form = useForm<FormInputs>({
    defaultValues: {
      email: '',
      level: 'member'
    }
  })

  // Create the invitation
  const mutation = useMutation({
    mutationKey: [ 'organizations', params.organization, 'invitations', 'create' ],
    mutationFn: (inputs: FormInputs) => invitationAPI.create({
      organization: params.organization,
      email: inputs.email,
      admin: inputs.level === 'admin'
    }),
    onSuccess: ({ invitation }) => {
      queryClient.invalidateQueries({
        queryKey: [ 'organizations', params.organization, 'invitations' ]
      })
      toast.success(t('new_invitation.success', { email: invitation.email }))
      close()
    }
  })

  return (
    <Dialog open modal onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('new_invitation.title')}</DialogTitle>
          <DialogDescription>{t('new_invitation.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(inputs => mutation.mutate(inputs))}
            className="flex flex-col gap-4"
          >

            {mutation.error && (
              <AlertBox
                error
                title={mutationError(mutation.error, {
                  default: t('new_invitation.errors.default'),
                  forbidden: t('new_invitation.errors.forbidden')
                })}
              />
            )}

            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      required
                      maxLength={128}
                      autoFocus
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="level"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.level')}</FormLabel>
                  <FormControl>
                    <Select defaultValue={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin" disabled={!grantableAccessLevels.admin}>
                          {t('fields.level_enum.admin')}
                        </SelectItem>
                        <SelectItem value="member">
                          {t('fields.level_enum.member')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={close}>
                {t('general:cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {t('new_invitation.submit')}
              </Button>
            </DialogFooter>

          </form>
        </Form>

      </DialogContent>
    </Dialog>
  )
}
